/**
 * 학부모 인증 Hook (자체 JWT 기반)
 * localStorage의 parent_access_token을 사용하여 인증 상태 관리
 */
import { useEffect, useState, useCallback } from 'react'
import { API_BASE_URL } from '../../../config/api'

interface Parent {
  id: string
  user_id: string
  name: string
  phone: string
  is_active?: boolean
  created_at?: string
}

// 더미 타입 (기존 인터페이스 호환)
type User = { id: string; email?: string; user_metadata?: Record<string, any> }
type Session = { access_token: string }

export function useSupabaseParentAuth() {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [parent, setParent] = useState<Parent | null>(null)
  const [loading, setLoading] = useState(true)

  const getStoredToken = (): string | null => {
    return localStorage.getItem('parent_access_token')
  }

  // 학부모 정보 조회
  const fetchParentInfo = useCallback(async (token: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/parent/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        // 토큰 만료 시 리프레시 시도
        if (response.status === 401) {
          const refreshed = await tryRefreshToken()
          if (refreshed) return // 리프레시 성공 시 재귀적으로 다시 호출됨
        }
        setParent(null)
        setUser(null)
        setSession(null)
        return
      }

      const result = await response.json()

      if (result.parent) {
        setParent(result.parent)
        setUser({ id: result.parent.user_id || result.parent.id, email: result.parent.phone })
        setSession({ access_token: token })
      } else {
        setParent(null)
      }
    } catch (error) {
      console.error('학부모 정보 조회 에러:', error)
      setParent(null)
    }
  }, [])

  // 토큰 리프레시 시도
  const tryRefreshToken = async (): Promise<boolean> => {
    const refreshToken = localStorage.getItem('parent_refresh_token')
    if (!refreshToken) return false

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/unified-auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken })
      })

      if (!response.ok) return false

      const data = await response.json()
      if (data.access_token) {
        localStorage.setItem('parent_access_token', data.access_token)
        if (data.refresh_token) {
          localStorage.setItem('parent_refresh_token', data.refresh_token)
        }
        // 새 토큰으로 다시 학부모 정보 조회
        await fetchParentInfo(data.access_token)
        return true
      }
    } catch (error) {
      console.error('토큰 리프레시 실패:', error)
    }
    return false
  }

  // 초기화
  useEffect(() => {
    const init = async () => {
      const token = getStoredToken()
      if (token) {
        await fetchParentInfo(token)
      }
      setLoading(false)
    }
    init()
  }, [fetchParentInfo])

  // 로그인 (레거시 호환 - UnifiedLoginPage가 이미 처리)
  const signIn = async (phone: string, password: string): Promise<{ data: any, parent: Parent | null }> => {
    const normalizedPhone = phone.replace(/-/g, '')

    const response = await fetch(`${API_BASE_URL}/api/v1/unified-auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone: normalizedPhone, password })
    })

    if (!response.ok) {
      const err = await response.json()
      throw new Error(err.detail || '로그인에 실패했습니다.')
    }

    const data = await response.json()
    localStorage.setItem('parent_access_token', data.access_token)
    localStorage.setItem('parent_refresh_token', data.refresh_token)
    localStorage.setItem('parent_info', JSON.stringify(data.user))
    localStorage.setItem('unified_role', data.role)

    await fetchParentInfo(data.access_token)
    return { data, parent }
  }

  // 카카오 로그인 (placeholder)
  const signInWithKakao = async () => {
    throw new Error('카카오 로그인은 현재 지원하지 않습니다')
  }

  // 로그아웃
  const signOut = async () => {
    localStorage.removeItem('parent_access_token')
    localStorage.removeItem('parent_refresh_token')
    localStorage.removeItem('parent_info')
    localStorage.removeItem('unified_role')
    setUser(null)
    setSession(null)
    setParent(null)
  }

  // 비밀번호 재설정
  const resetPassword = async (phone: string) => {
    throw new Error('전화번호 기반 비밀번호 재설정은 현재 지원하지 않습니다.')
  }

  // access_token 반환
  const getAccessToken = useCallback((): string | null => {
    return getStoredToken()
  }, [])

  return {
    user,
    session,
    parent,
    loading,
    signIn,
    signInWithKakao,
    signOut,
    resetPassword,
    getAccessToken,
    accessToken: getStoredToken(),
    isAuthenticated: !!user || !!getStoredToken(),
    isPhoneLinked: !!parent
  }
}
